import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, FileText, Bot, User, Send } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import FeedbackModal from '../components/ui/FeedbackModal';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import PDFViewer from '../components/PDFViewer';
import PDFUpload from '../components/PDFUpload';
import { firestoreService, Policy } from '../services/firestoreService';
import { aiChatService } from '../services/aiChatService';
import { ragApiService } from '../services/ragApiService';
import { authService, AuthUser } from '../services/authService';
import { ReanalysisResult } from '../services/existingPolicyReanalysisService';
import { useResponsive } from '../hooks/useResponsive';
import MobileLayout from '../components/mobile/MobileLayout';
import { trackButtonClick, trackAIChatEvent, trackConversion, trackUserJourney } from '../utils/analytics'; // Analytics imports
import { logAIChatEvent, logPolicyEvent, logConversion as logFirebaseConversion, logUserJourney as logFirebaseJourney, logFeatureUsage } from '../utils/firebaseAnalytics'; // Firebase Analytics imports
import { initUserBehaviorTracking, trackUserBehavior, trackSpecificBehaviors, setUserAttributes } from '../utils/userBehaviorTracking'; // User behavior tracking
import { startChatSession, trackChatMessage } from '../utils/chatAnalytics'; // Chat analytics
import { setupUserFiltering, shouldTrackEvent, shouldTrackChat, filterEventData } from '../utils/userFiltering'; // User filtering
import EnvironmentBanner from '../components/EnvironmentBanner'; // Environment banner
import '../utils/networkTest';

// 使用 Firestore 的 Policy 接口

// TODO: 定義聊天訊息型別
interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isTyping?: boolean;
}


// 固定分類，根據保單性質分類
const categories = ['全部', '人壽', '醫療', '財產', '投資', '特殊'];

// 根據保單性質分類到固定分類
const categorizePolicy = (policy: Policy): string => {
  const category = policy.category?.toLowerCase() || '';
  const name = policy.name?.toLowerCase() || '';
  
  // 人壽保險 - 包含壽險、意外險、重大疾病險
  if (category.includes('life') || category.includes('personal') ||
      name.includes('life') || name.includes('personal') ||
      name.includes('壽險') || name.includes('人壽') ||
      category.includes('critical illness') || name.includes('critical illness') ||
      name.includes('重大疾病') || name.includes('癌症') ||
      category.includes('accident') || name.includes('accident') ||
      name.includes('意外') || name.includes('傷害')) {
    return '人壽';
  }
  
  // 醫療保險 - 包含健康險、醫療險、牙科險
  if (category.includes('health') || category.includes('medical') || 
      name.includes('health') || name.includes('medical') ||
      name.includes('醫療') || name.includes('健康') ||
      category.includes('dental') || name.includes('dental') ||
      name.includes('牙科') || name.includes('住院') ||
      name.includes('手術') || name.includes('門診')) {
    return '醫療';
  }
  
  // 財產保險 - 包含車險、房屋險、寵物險
  if (category.includes('property') || category.includes('auto') ||
      name.includes('property') || name.includes('auto') ||
      name.includes('財產') || name.includes('車險') ||
      name.includes('汽車') || name.includes('房屋') ||
      name.includes('住宅') || name.includes('火險') ||
      ((category.includes('pet') || name.includes('pet')) && !name.includes('liability')) ||
      name.includes('寵物')) {
    return '財產';
  }
  
  // 投資保險 - 包含儲蓄險、年金險、投資型保險
  if (category.includes('investment') || category.includes('savings') ||
      name.includes('investment') || name.includes('savings') ||
      name.includes('投資') || name.includes('儲蓄') ||
      name.includes('年金') || name.includes('養老') ||
      name.includes('退休') || name.includes('理財') ||
      name.includes('分紅') || name.includes('萬能')) {
    return '投資';
  }
  
  // 特殊保險 - 不屬於以上四類或橫跨多類別
  return '特殊';
};

const quickQuestions = [
  '什麼是SurePal？',
  '我的理賠限額是多少？',
  '我的保單還有效嗎？',
  '如何申請理賠？',
  '我的保單何時到期？'
];

const MainPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '' });
  
  // 刪除確認模態框狀態
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<Policy | null>(null);
  
  
  
  // 保單展開狀態 - 暫時不使用，未來可能恢復
  const [expandedPolicy, setExpandedPolicy] = useState<Policy | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  
  // 共享的聊天狀態
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: '您好！我是您的AI保險助手。今天有什麼可以幫助您的嗎？',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isAIReplying, setIsAIReplying] = useState(false); // AI回覆狀態
  
  // 載入用戶政策數據
  const loadUserPolicies = useCallback(async (userId: string) => {
    try {
      console.log('🔄 開始載入用戶保單數據...');
      const userPolicies = await firestoreService.getUserPolicies(userId);
      setPolicies(userPolicies);
      console.log(`✅ 成功載入 ${userPolicies.length} 個用戶保單`);
      
      // 自動分析現有保單的狀態和保費單位
      if (userPolicies.length > 0) {
        console.log('🔄 開始自動分析現有保單狀態...');
        await autoAnalyzeExistingPolicies(userId, userPolicies);
      }
    } catch (error) {
      console.error('❌ 載入用戶保單失敗:', error);
      // 如果用戶政策加載失敗，回退到公共政策
      try {
        console.log('🔄 回退到載入公共保單數據...');
        const policiesList = await firestoreService.getAllPolicies();
        
        if (policiesList.length > 0) {
          console.log(`✅ 成功載入 ${policiesList.length} 筆保單數據`);
          setPolicies(policiesList);
        } else {
          console.log('⚠️ Firestore 中沒有數據');
          setPolicies([]);
        }
      } catch (error) {
        console.error('❌ 載入保單失敗:', error);
        console.log('🔄 設置空數組作為備用');
        setPolicies([]);
        // 追蹤錯誤
        trackUserBehavior.error('policy_loading_error', error instanceof Error ? error.message : '未知錯誤', 'MainPage.loadPolicies');
      }
    }
  }, []);

  // 認證狀態監聽和載入用戶數據
  useEffect(() => {
    // 監聽認證狀態變化
    const unsubscribe = authService.onAuthStateChanged((authUser: AuthUser | null) => {
      setUser(authUser);
      if (authUser) {
        console.log('用戶已登入:', authUser.uid);
        loadUserPolicies(authUser.uid);
      } else {
        console.log('用戶未登入');
        setPolicies([]);
      }
    });

    return () => unsubscribe();
  }, [loadUserPolicies]);

  // 初始化用戶行為追蹤
  useEffect(() => {
    // 初始化用戶行為追蹤
    initUserBehaviorTracking();
    
    // 設置用戶過濾
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setupUserFiltering(undefined, sessionId);
    
    // 設置用戶屬性
    setUserAttributes.deviceType(isMobile ? 'mobile' : 'desktop');
    setUserAttributes.userType('new'); // 可以根據實際情況調整
    
    // 開始聊天會話追蹤
    if (shouldTrackChat()) {
      startChatSession(sessionId, {
        device_type: isMobile ? 'mobile' : 'desktop',
        has_policies: policies.length > 0,
      });
    }
    
    // 追蹤頁面瀏覽（過濾後）
    if (shouldTrackEvent()) {
      trackUserBehavior.pageView('main_page', filterEventData({
        is_mobile: isMobile,
        has_policies: policies.length > 0,
      }));
    }

    // loadPolicies(); // 已移至認證狀態監聽中
  }, [isMobile, policies.length]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 智能滾動到最新消息
  const scrollToBottom = useCallback((force = false) => {
    if (chatContainerRef.current && (shouldAutoScroll || force)) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [shouldAutoScroll]);

  // 檢測用戶是否在底部
  const isNearBottom = () => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100; // 100px容差
  };

  // 處理滾動事件
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const isAtBottom = isNearBottom();
    
    // 清除之前的定時器
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // 設置用戶滾動狀態
    setIsUserScrolling(true);
    
    // 如果用戶滾動到頂部，停止自動滾動
    if (chatContainerRef.current.scrollTop < 50) {
      setShouldAutoScroll(false);
    } else if (isAtBottom) {
      // 如果用戶滾動回底部，重新啟用自動滾動
      setShouldAutoScroll(true);
    }
    
    // 1秒後重置用戶滾動狀態
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 1000);
  };

  // 建議問題滾動功能
  const scrollQuestions = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      setScrollPosition(newPosition);
    }
  };

  // 當消息更新時智能滾動
  useEffect(() => {
    // 只有在用戶沒有手動滾動時才自動滾動
    if (shouldAutoScroll && !isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll, isUserScrolling, scrollToBottom]);

  // 當用戶發送新消息時，重新啟用自動滾動
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'user') {
      // 用戶發送新消息時，重新啟用自動滾動並滾動到底部
      setShouldAutoScroll(true);
      setIsUserScrolling(false);
      scrollToBottom(true);
    }
  }, [messages, scrollToBottom]);

  // 清理定時器
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // 過濾保單
  const filteredPolicies = policies.filter(policy => {
    if (selectedCategory === '全部') return true;
    const policyCategory = categorizePolicy(policy);
    return policyCategory === selectedCategory;
  });

  // 計算每個分類的保單數量
  const getCategoryCount = (category: string): number => {
    if (category === '全部') return policies.length;
    return policies.filter(policy => categorizePolicy(policy) === category).length;
  };

  // 計算總保費（只計算月繳型保單）
  const totalAnnualPremium = filteredPolicies.reduce((sum, policy) => {
    // 只計算月繳型保單的保費
    if (policy.premiumUnit === 'monthly') {
      return sum + (policy.annualPremium || 0);
    }
    return sum;
  }, 0);
  const totalMonthlyPremium = Math.round(totalAnnualPremium / 12);

  // 上傳按鈕點擊處理
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUploadClick = () => {
    // 追蹤上傳按鈕點擊
    trackButtonClick('Upload', 'Desktop');
    trackUserJourney('upload_button_clicked');
    
    // 追蹤上傳按鈕事件（不設置轉換價值）
    // trackConversion('upload_button_clicked', 1);
    
    // Firebase Analytics 追蹤
    logFeatureUsage('upload_button', { location: 'desktop' });
    logFirebaseJourney('upload_button_clicked', { 
      policies_count: policies.length,
      has_policies: policies.length > 0 
    });

    // 新的用戶行為追蹤
    trackUserBehavior.interaction('button_click', 'upload_button', {
      button_location: 'desktop',
      policies_count: policies.length,
      has_policies: policies.length > 0,
    });
    trackUserBehavior.journeyStep('upload_button_clicked', {
      policies_count: policies.length,
      has_policies: policies.length > 0,
    });
    
    setModalConfig({
      title: '加入等候名單',
      message: '這是測試版本，功能有限，僅供演示使用！\n\n未來版本將支援PDF保單文件上傳。\n\n請點擊「加入等候名單」以獲取開發進度更新！'
    });
    setShowFeedbackModal(true);
  };

  // 處理查看保單詳情 - 直接在新窗口打開PDF
  const handleViewPolicyClick = (policy: Policy) => {
    // 追蹤查看詳情按鈕點擊
    trackButtonClick('View Policy', 'Desktop');
    trackUserJourney('view_policy_clicked');
    
    // Firebase Analytics 追蹤
    logPolicyEvent('view_policy_clicked', { 
      policies_count: policies.length,
      selected_category: selectedCategory,
      policy_name: policy.name
    });
    logFirebaseJourney('view_policy_clicked', { 
      policies_count: policies.length,
      selected_category: selectedCategory,
      policy_name: policy.name
    });
    
    // 直接在新窗口打開PDF
    if (policy.pdfUrl) {
      window.open(policy.pdfUrl, '_blank');
    } else {
      console.error('PDF URL不存在:', policy);
      alert('PDF文件不存在，請重新上傳');
    }
  };

  // 關閉展開的保單 - 暫時不使用，未來可能恢復
  const handleCloseExpandedPolicy = () => {
    setExpandedPolicy(null);
  };

  // 處理反饋提交
  const handleFeedback = () => {
    // 追蹤轉換事件
    trackConversion('feedback_submitted', 1);
    trackUserJourney('feedback_submitted');

    // Firebase Analytics 追蹤
    logFirebaseConversion('feedback_submitted', 1, {
      policies_count: policies.length,
      total_premium: totalAnnualPremium,
      has_used_ai_chat: messages.some(msg => msg.sender === 'user')
    });
    logFirebaseJourney('feedback_submitted', {
      policies_count: policies.length,
      total_premium: totalAnnualPremium,
      has_used_ai_chat: messages.some(msg => msg.sender === 'user')
    });

    window.open('https://cop855u0.forms.app/surepal-waitlist', '_blank');
    setShowFeedbackModal(false);
  };

  // 處理刪除保單
  const handleDeletePolicy = (policy: Policy) => {
    setPolicyToDelete(policy);
    setShowDeleteModal(true);
  };

  // 確認刪除保單
  const handleConfirmDelete = async () => {
    if (!policyToDelete || !user) return;

    try {
      // 從Firebase刪除保單數據
      await firestoreService.deleteUserPolicy(user.uid, policyToDelete.id);
      
      // 從Firebase Storage刪除PDF文件
      if (policyToDelete.pdfUrl) {
        try {
          const { deleteObject, ref } = await import('firebase/storage');
          const { storage } = await import('../firebase');
          const fileRef = ref(storage, policyToDelete.pdfUrl);
          await deleteObject(fileRef);
          console.log('PDF文件已從Storage刪除');
        } catch (storageError) {
          console.warn('刪除PDF文件失敗:', storageError);
          // 即使Storage刪除失敗，也繼續刪除Firestore數據
        }
      }
      
      // 從本地狀態中移除
      setPolicies(prev => prev.filter(p => p.id !== policyToDelete.id));
      
      console.log('保單已刪除:', policyToDelete.name);
      setShowDeleteModal(false);
      setPolicyToDelete(null);
    } catch (error) {
      console.error('刪除保單失敗:', error);
      // 顯示錯誤提示
      alert('刪除保單失敗，請稍後再試');
    }
  };

  // 取消刪除
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPolicyToDelete(null);
  };

  // 自動檢查現有保單到期狀態（優化版本 - 不使用AI）
  const autoAnalyzeExistingPolicies = async (userId: string, policies: Policy[]) => {
    try {
      if (policies.length === 0) {
        console.log('✅ 沒有保單需要檢查');
        return;
      }
      
      console.log(`🔄 開始檢查所有 ${policies.length} 個保單的到期狀態（使用日期比較，不使用AI）`);
      
      // 導入保單到期檢查服務
      const { policyExpiryService } = await import('../services/policyExpiryService');
      
      // 批量檢查保單到期狀態
      const expiryResults = policyExpiryService.checkMultiplePolicies(
        policies.map(policy => ({
          id: policy.id,
          name: policy.name,
          expiryDate: policy.expiryDate,
          effectiveDate: policy.effectiveDate,
          premiumUnit: policy.premiumUnit
        }))
      );
      
      console.log('📊 保單到期檢查結果:', expiryResults);
      
      // 更新有狀態變化的保單
      const updatePromises = expiryResults
        .filter(result => {
          const originalPolicy = policies.find(p => p.id === result.id);
          return originalPolicy && originalPolicy.status !== result.status;
        })
        .map(async (result) => {
          console.log(`🔄 更新保單狀態: ${result.name} -> ${result.status} (${result.reason})`);
          return firestoreService.updateUserPolicy(userId, result.id, {
            status: result.status
          });
        });
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        console.log(`✅ 已更新 ${updatePromises.length} 個保單的狀態`);
        
        // 重新載入保單數據以顯示更新後的狀態
        const updatedPolicies = await firestoreService.getUserPolicies(userId);
        console.log('🔄 重新載入的保單數據:', updatedPolicies.map(p => ({
          id: p.id,
          name: p.name,
          status: p.status,
          premiumUnit: p.premiumUnit,
          expiryDate: p.expiryDate
        })));
        setPolicies(updatedPolicies);
      } else {
        console.log('✅ 所有保單狀態無需更新');
      }
      
    } catch (error) {
      console.error('❌ 檢查保單到期狀態失敗:', error);
    }
  };

  // 處理現有保單重新分析完成
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReanalysisComplete = async (results: ReanalysisResult[]) => {
    try {
      // 重新載入保單數據以顯示更新後的狀態
      if (user) {
        const updatedPolicies = await firestoreService.getUserPolicies(user.uid);
        setPolicies(updatedPolicies);
      }
      
      console.log('現有保單重新分析完成:', results);
    } catch (error) {
      console.error('更新保單數據失敗:', error);
    }
  };


  // 關閉彈出視窗
  const handleCloseModal = () => {
    setShowFeedbackModal(false);
  };

  // 處理登出
  const handleLogout = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setPolicies([]);
      console.log('登出成功');
    } catch (error) {
      console.error('登出失敗:', error);
    }
  };

  // 處理PDF上傳完成
  const handleUploadComplete = async (newPolicy: Policy) => {
    console.log('PDF上傳完成:', newPolicy);
    setPolicies(prevPolicies => [newPolicy, ...prevPolicies]);
    
    // 重新加載用戶保單數據以確保同步
    if (user) {
      try {
        console.log('🔄 重新加載用戶保單數據...');
        await loadUserPolicies(user.uid);
      } catch (error) {
        console.error('❌ 重新加載保單數據失敗:', error);
      }
    }
  };

  // 處理上傳錯誤
  const handleUploadError = (error: string) => {
    console.error('PDF上傳失敗:', error);
    // 可以添加錯誤提示
  };

  // 逐字生成AI回應
  const typeMessage = (messageId: string, fullText: string) => {
    console.log('🖊️ 開始打字機效果:', { messageId, textLength: fullText.length });
    let currentIndex = 0;
    
    // 先清空內容，確保從空開始
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, content: '', isTyping: true }
        : msg
    ));
    
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: fullText.slice(0, currentIndex + 1), isTyping: true }
            : msg
        ));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, isTyping: false }
            : msg
        ));
        console.log('✅ 打字機效果完成');
        
        // 追蹤AI回應完成（過濾後）
        if (shouldTrackChat()) {
          const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const responseTime = Date.now(); // 簡化回應時間計算
          trackChatMessage(sessionId, fullText, messageId, 'ai', responseTime);
        }
        
        // 打字機效果完成後，重置AI回覆狀態
        setIsAIReplying(false);
      }
    }, 15); // 每個字15ms，類似ChatGPT的速度
  };

  // AI 回應邏輯
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isAIReplying) return; // 防止重複發送

    // 追蹤AI聊天事件
    trackAIChatEvent('message_sent', {
      message_length: inputMessage.length,
      has_policies: policies.length > 0
    });
    trackUserJourney('ai_chat_used');

    // 追蹤AI聊天事件（不設置轉換價值）
    // trackConversion('ai_chat_used', 1);

    // Firebase Analytics 追蹤
    logAIChatEvent('message_sent', {
      message_length: inputMessage.length,
      policies_count: policies.length,
      has_policies: policies.length > 0,
      message_preview: inputMessage.substring(0, 50) // 只記錄前50個字符
    });
    logFirebaseJourney('ai_chat_used', {
      message_length: inputMessage.length,
      policies_count: policies.length,
      total_messages: messages.length + 1
    });

    // 新的用戶行為追蹤
    trackSpecificBehaviors.aiChat.messageSent(inputMessage.length, policies.length > 0);
    trackUserBehavior.journeyStep('ai_message_sent', {
      message_length: inputMessage.length,
      policies_count: policies.length,
      has_policies: policies.length > 0,
    });

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    // 追蹤聊天內容（過濾後）
    if (shouldTrackChat()) {
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      trackChatMessage(sessionId, inputMessage, userMessage.id, 'user');
    }

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');

    // 設置AI回覆狀態
    setIsAIReplying(true);

    // 顯示AI正在輸入
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
      content: '',
        sender: 'ai',
      timestamp: new Date(),
      isTyping: true
      };
      setMessages(prev => [...prev, aiResponse]);

    try {
      // 使用新的 RAG API
      if (!user || !user.uid) {
        throw new Error('用戶未登入');
      }

      console.log('📡 調用 RAG API，用戶 ID:', user.uid);
      const ragResponse = await ragApiService.chat({
        userId: user.uid,
        message: currentInput
      });

      console.log('✅ RAG API 回應:', {
        hasRelevantData: ragResponse.hasRelevantData,
        sourcesCount: ragResponse.sources.length,
        replyLength: ragResponse.reply.length
      });

      // 直接使用 AI 回答，不顯示參考來源
      typeMessage(aiResponse.id, ragResponse.reply);
    } catch (error: any) {
      console.error('❌ AI回應失敗:', error);
      
      // 如果 RAG API 失敗，回退到舊的 AI 服務
      try {
        console.log('🔄 回退到舊的 AI 服務...');
        const userId = user?.uid || `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const aiResponseText = await aiChatService.processMessage(currentInput, policies, userId);
        console.log('🤖 AI回應文本:', aiResponseText);
        typeMessage(aiResponse.id, aiResponseText);
      } catch (fallbackError) {
        console.error('❌ 回退服務也失敗:', fallbackError);
        const errorMessage = '抱歉，AI服務暫時不可用。請稍後再試。';
        typeMessage(aiResponse.id, errorMessage);
      }
    }
    // 注意：不在這裡重置isAIReplying，讓typeMessage函數在打字機效果完成後重置
  };


  // TODO: 實作等待清單提交功能


  // 如果是手機版，使用MobileLayout
  if (isMobile) {
    return (
      <MobileLayout 
        policies={policies}
        messages={messages}
        setMessages={setMessages}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        typeMessage={typeMessage}
        handleSendMessage={handleSendMessage}
        quickQuestions={quickQuestions}
        scrollPosition={scrollPosition}
        scrollContainerRef={scrollContainerRef}
        scrollQuestions={scrollQuestions}
        chatContainerRef={chatContainerRef}
        isUserScrolling={isUserScrolling}
        shouldAutoScroll={shouldAutoScroll}
        handleScroll={handleScroll}
        scrollToBottom={scrollToBottom}
        isAIReplying={isAIReplying}
      />
    );
  }

  return (
    <>
      <EnvironmentBanner />
      <div className="h-screen flex flex-col" style={{ background: 'var(--main-background)', maxWidth: '100vw', overflowX: 'hidden', overflowY: 'hidden', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b" style={{ borderColor: 'var(--card-shadow)' }}>
        <div className="w-full px-6">
          <div className="h-16 flex items-center justify-between">
          <Logo className="flex-shrink-0" />
            <div className="flex items-center gap-4">
              {/* 用戶資訊 */}
              {user && (
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                      {user.email}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--medium-gray)' }}>
                      ID: {user.uid?.substring(0, 8)}...
                    </p>
                  </div>
                  
                  {/* 登出按鈕 */}
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={handleLogout}
                  >
                    登出
                  </Button>
                </div>
              )}
              
              
              {/* Feedbacks 按鈕 */}
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => {
                  setModalConfig({
                    title: '意見反饋',
                    message: '我們重視您的意見！這是測試版本，功能有限，僅供演示使用！\n\n未來版本將支援更多功能。\n\n請分享您的想法，幫助我們改進！'
                  });
                  setShowFeedbackModal(true);
                }}
              >
                意見反饋
            </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex gap-6" style={{ overflow: 'hidden', paddingTop: '24px', paddingRight: '24px', paddingBottom: '24px', paddingLeft: '24px', height: 'calc(100vh - 89px)', boxSizing: 'border-box' }}>
          {/* Left Side - Categories */}
          <div className="sidebar flex flex-col" style={{ width: '160px', height: '100%', flexShrink: 0 }}>
            {/* Navigation Section - Categories */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="space-y-2 flex flex-col items-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                      className={`w-full text-left nav-item flex justify-between items-center ${
                        selectedCategory === category ? 'active' : ''
                    }`}
                  >
                    <span>{category}</span>
                    <span className="text-xs opacity-60">({getCategoryCount(category)})</span>
                  </button>
                ))}
                </div>
              </div>
            </div>
            
            {/* Upload Section - Fixed at bottom */}
            <div className="px-4 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--card-shadow)' }}>
              {user ? (
                <div className="space-y-4">
                  <PDFUpload
                    userId={user.uid}
                    onUploadComplete={handleUploadComplete}
                    onError={handleUploadError}
                  />
                </div>
              ) : (
                <Button 
                  variant="default"
                  className="w-full h-12 flex items-center justify-center px-0 relative overflow-hidden transition-all duration-200"
                  onClick={() => {
                    console.log('請先登入以上傳保單');
                  }}
                  style={{
                    background: 'var(--medium-gray)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 20, 20, 0.15)'
                  }}
                >
                  <div className="flex items-center justify-center gap-3">
                    <Upload className="h-5 w-5" />
                    <span className="font-medium">請先登入</span>
                  </div>
                </Button>
              )}
            </div>
          </div>

          {/* Right Side - Combined Summary and Policy List */}
          <div className="flex-1 flex flex-col min-w-0" style={{ overflow: 'hidden', height: '100%', minHeight: 0, flexShrink: 0 }}>
            {/* Summary Header - Fixed at top */}
            <div className="card-header" style={{ height: '65px', display: 'flex', alignItems: 'center', padding: '0 24px' }}>
              <div className="flex justify-between items-center w-full">
                <div>
                  <h2 className="text-heading2 mb-1">
                    {selectedCategory === '全部' ? '所有保單' : selectedCategory}
                  </h2>
                  <p className="text-caption">
                    {filteredPolicies.length} 個保單
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-heading2 mb-1" style={{ color: 'var(--charcoal)' }}>
                    ${totalMonthlyPremium.toLocaleString()}
                  </div>
                  <div className="text-caption" style={{ color: 'var(--medium-gray)' }}>
                    per month
                  </div>
                </div>
              </div>
            </div>
            
            {/* Policy List */}
            <div 
              className="flex-1 space-y-4 scrollbar-auto relative" 
              style={{ 
                padding: '0 20px', 
                margin: '0 -20px', 
                height: 'calc(100% - 65px)', 
                overflowY: expandedPolicy ? 'hidden' : 'auto', 
                overflowX: 'visible' 
              }}
            >
              {filteredPolicies.length === 0 ? (
                <div className="text-center py-16">
                  <FileText className="h-16 w-16 mx-auto mb-6" style={{ color: 'var(--light-gray)' }} />
                  <p className="text-body-large">此分類中沒有保單</p>
                  <p className="text-caption mt-2">上傳保單文件開始管理</p>
                </div>
              ) : (
                filteredPolicies.map((policy) => {
                  // 如果有展開的保單，只顯示該保單
                  if (expandedPolicy && expandedPolicy.id !== policy.id) {
                    return null;
                  }
                  
                  return (
                  <Card 
                    key={policy.id} 
                    className={`${
                      expandedPolicy?.id === policy.id 
                        ? 'fixed top-0 left-0 z-50 m-0 expanded-policy-card' 
                        : 'transition-medium'
                    }`}
                    style={{
                      height: expandedPolicy?.id === policy.id ? 'calc(100vh - 65px - 48px)' : 'auto',
                      width: expandedPolicy?.id === policy.id ? 'calc(45% - 200px)' : 'auto',
                      left: expandedPolicy?.id === policy.id ? 'calc(160px + 24px + 16px)' : 'auto',
                      top: expandedPolicy?.id === policy.id ? 'calc(65px + 24px)' : 'auto',
                      backgroundColor: expandedPolicy?.id === policy.id ? 'var(--pure-white)' : 
                                     policy.status === 'expired' ? '#e8f5f5' : undefined,
                      borderRadius: expandedPolicy?.id === policy.id ? '12px' : undefined
                    }}
                  >
                    {expandedPolicy?.id === policy.id ? (
                      // 展開狀態 - 顯示PDF查看器
        <PDFViewer
          pdfUrl={policy.pdfUrl || ''}
          onClose={handleCloseExpandedPolicy}
          policyName={policy.name}
          companyName={policy.company || '未知公司'}
        />
                    ) : (
                      // 正常狀態 - 顯示卡片內容
                      <>
                        <CardHeader className="pb-4">
                      <div className="space-y-2">
                        {/* 第一行：保單名稱 + 月繳金額 */}
                        <div className="flex items-center">
                          <div className="flex-1 relative overflow-hidden">
                            <CardTitle className="text-heading3 whitespace-nowrap pr-2">{policy.name}</CardTitle>
                            <div 
                              className="absolute top-0 right-0 bottom-0 w-8 pointer-events-none"
                              style={{
                                background: policy.status === 'expired' 
                                  ? 'linear-gradient(to left, #e8f5f5 0%, transparent 100%)'
                                  : 'linear-gradient(to left, var(--pure-white) 0%, transparent 100%)'
                              }}
                            />
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <div className="text-heading3 whitespace-nowrap" style={{ color: 'var(--charcoal)' }}>
                              ${policy.premiumUnit === 'one-time' 
                                ? Math.round(policy.annualPremium || 0)
                                : Math.round((policy.annualPremium || 0) / 12)
                              }
                              {policy.premiumUnit === 'one-time' ? '元/次' : '元/月'}
                            </div>
                          </div>
                        </div>
                        
                        {/* 第二行：保險公司 + Monthly premium */}
                        <div className="flex items-center">
                          <div className="flex-1 relative overflow-hidden">
                            <CardDescription className="text-caption whitespace-nowrap pr-2">
                              {policy.company || '未知公司'}
                            </CardDescription>
                            <div 
                              className="absolute top-0 right-0 bottom-0 w-8 pointer-events-none"
                              style={{
                                background: policy.status === 'expired' 
                                  ? 'linear-gradient(to left, #e8f5f5 0%, transparent 100%)'
                                  : 'linear-gradient(to left, var(--pure-white) 0%, transparent 100%)'
                              }}
                            />
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <div 
                              className="text-caption whitespace-nowrap px-2 py-1 rounded-md border"
                              style={{
                                color: policy.status === 'expired' ? '#001414' : '#00e5e6',
                                borderColor: policy.status === 'expired' ? '#001414' : '#00e5e6',
                                backgroundColor: 'transparent'
                              }}
                            >
                              {policy.status === 'expired' ? '已到期' : '保障中'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewPolicyClick(policy)}
                          className="flex-1 h-10 flex items-center justify-center rounded-lg border transition-colors duration-200 hover:bg-gray-50"
                          style={{
                            backgroundColor: policy.status === 'expired' ? '#e8f5f5' : 'var(--pure-white)',
                            borderColor: 'var(--card-shadow)',
                            color: 'var(--medium-gray)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = policy.status === 'expired' ? '#d1f2eb' : 'var(--very-light-gray)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = policy.status === 'expired' ? '#e8f5f5' : 'var(--pure-white)';
                          }}
                        >
                          查看保單
                        </button>
                        <button
                          onClick={() => handleDeletePolicy(policy)}
                          className="w-10 h-10 flex items-center justify-center rounded-lg border transition-colors duration-200 hover:bg-red-50 hover:border-red-300"
                          style={{
                            borderColor: 'var(--card-shadow)',
                            color: 'var(--medium-gray)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--medium-gray)';
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </CardContent>
                      </>
                    )}
                  </Card>
                  );
                })
              )}
          </div>
        </div>

          {/* Right Sidebar - AI Chat */}
          <div className="sidebar flex flex-col min-w-0" style={{ width: 'calc(55% - 12px - 12px + 8px)', height: '100%', minHeight: 0, flexShrink: 0 }}>
            {/* Chat Header */}
            <div className="px-6 py-4 border-b flex items-center" style={{ borderColor: 'var(--card-shadow)', height: '65px' }}>
            <div className="flex items-center">
                <Bot className="h-6 w-6 mr-3" style={{ color: 'var(--medium-gray)' }} />
                <h2 className="text-heading3">AI Pal</h2>
            </div>
          </div>

            {/* Chat Messages */}
            <div 
              ref={chatContainerRef} 
              className="flex-1 overflow-y-auto px-6 py-4 space-y-4" 
              style={{ minHeight: 0 }}
              onScroll={handleScroll}
            >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                    className={`flex items-start space-x-3 max-w-[90%] ${
                    message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      message.sender === 'user'
                          ? 'text-white'
                          : 'text-white'
                    }`}
                      style={{
                        backgroundColor: message.sender === 'user' ? 'var(--charcoal)' : 'var(--light-gray)'
                      }}
                  >
                    {message.sender === 'user' ? (
                        <User className="h-5 w-5" />
                    ) : (
                        <Bot className="h-5 w-5" />
                    )}
                  </div>
                  <div
                      className={`px-4 py-3 rounded-2xl ${
                      message.sender === 'user'
                          ? 'bg-white shadow-sm'
                          : 'bg-white'
                      }`}
                      style={{ border: '1px solid var(--card-shadow)' }}
                    >
                      {message.sender === 'ai' && message.isTyping && !message.content ? (
                        <div className="flex items-center">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-body whitespace-pre-wrap leading-relaxed">
                          {message.content}
                          {message.sender === 'ai' && message.isTyping && message.content && (
                            <span className="inline-block w-0.5 h-4 bg-gray-600 ml-0.5" style={{
                              animation: 'blink 1s infinite'
                            }}></span>
                          )}
                        </p>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>

            {/* Quick Questions */}
            <div className="px-6 py-6 border-t flex items-center relative" style={{ borderColor: 'var(--card-shadow)' }}>
              {/* 左箭頭 */}
              <button
                onClick={() => scrollQuestions('left')}
                className="absolute left-2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
                style={{ display: scrollPosition > 0 ? 'block' : 'none' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* 右箭頭 */}
              <button
                onClick={() => scrollQuestions('right')}
                className="absolute right-2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* 滾動容器 */}
              <div 
                ref={scrollContainerRef}
                className="flex gap-2 w-full overflow-x-auto scrollbar-hide"
                style={{ 
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  paddingLeft: scrollPosition > 0 ? '40px' : '0',
                  paddingRight: '40px'
                }}
              >
              {quickQuestions.map((question) => (
                <Button
                  key={question}
                  variant="outline"
                  size="sm"
                  onClick={() => setInputMessage(question)}
                    className="badge-primary text-xs px-3 py-2 h-8 flex items-center justify-center whitespace-nowrap flex-shrink-0"
                >
                  {question}
                </Button>
              ))}
            </div>

              {/* 右側漸層遮罩 */}
              <div 
                className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none"
                style={{
                  background: 'linear-gradient(to left, var(--pure-white) 0%, transparent 100%)'
                }}
              />
          </div>

                   {/* Input Area */}
                   <div className="px-6 py-6 border-t flex items-center" style={{ borderColor: 'var(--card-shadow)' }}>
                     <form onSubmit={handleSendMessage} className="flex space-x-3 w-full items-center">
              <Input
                value={inputMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputMessage(e.target.value)}
                  placeholder="詢問我關於您的保險..."
                  className="flex-1 h-12 flex items-center"
              />
              <Button 
                type="submit" 
                         disabled={!inputMessage.trim() || isAIReplying}
                         className="h-12 w-12 p-0 flex items-center justify-center relative overflow-hidden transition-all duration-200"
                  style={{
                    background: 'var(--charcoal)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 20, 20, 0.15)'
                  }}
                         onMouseEnter={(e) => {
                           if (!inputMessage.trim() || isAIReplying) return; // 如果沒有文字或AI正在回覆，不應用效果
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 229, 230, 0.15)';
                    e.currentTarget.style.position = 'relative';
                    e.currentTarget.style.overflow = 'hidden';
                    
                    // 創建亮藍色旋光效果（淡出，不持續動畫）
                    const glowElement = document.createElement('div');
                    glowElement.style.cssText = `
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      background: linear-gradient(135deg, rgba(0, 229, 230, 0.8) 0%, rgba(0, 20, 20, 0.6) 50%, rgba(0, 229, 230, 0.3) 100%);
                      pointer-events: none;
                      z-index: 1;
                      opacity: 0;
                      transition: opacity 0.3s ease-in-out;
                    `;
                    glowElement.className = 'send-glow-effect';
                    e.currentTarget.appendChild(glowElement);
                    
                    // 淡入效果
                    setTimeout(() => {
                      glowElement.style.opacity = '1';
                    }, 10);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 20, 20, 0.15)';
                    
                    // 淡出效果
                    const glowElement = e.currentTarget.querySelector('.send-glow-effect') as HTMLElement;
                    if (glowElement) {
                      glowElement.style.opacity = '0';
                      setTimeout(() => {
                        if (glowElement.parentNode) {
                          glowElement.parentNode.removeChild(glowElement);
                        }
                      }, 300);
                    }
                  }}
              >
                <Send className="h-5 w-5" style={{ position: 'relative', zIndex: 2 }} />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={handleCloseModal}
        onFeedback={handleFeedback}
        title={modalConfig.title}
        message={modalConfig.message}
      />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        policyName={policyToDelete?.name}
        title="刪除保單"
        message={policyToDelete ? `您確定要刪除「${policyToDelete.name}」嗎？此操作無法復原。` : undefined}
      />


    </div>
    </>
  );
};

export default MainPage;
